import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Apple, Play, Mail, Timer, Lock } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { APP_URL, TESTFLIGHT_URL, ANDROID_TEST_URL } from "@/lib/assets";
import { useLang } from "@/i18n/LanguageContext";

const MAX_LAUNCHES = 2;
const DURATION_MS = 3 * 60 * 1000; // 3 minutes
const STORAGE_KEY = "josfit_launch_count";

const AppLaunchContext = createContext(null);
export const useAppLaunch = () => useContext(AppLaunchContext);

const TesterButtons = () => {
  const { t } = useLang();
  return (
    <div className="mt-6 flex flex-col gap-3">
      <a
        href={TESTFLIGHT_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="gate-testflight"
        className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-slate-900 hover:bg-slate-100 transition-colors"
      >
        <Apple className="h-5 w-5" /> {t.launch.testflight}
      </a>
      <a
        href={ANDROID_TEST_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="gate-android"
        className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-bold text-white hover:border-emerald-400/50 transition-colors"
      >
        <Play className="h-5 w-5" /> {t.launch.android}
      </a>
      <a
        href="mailto:contact@josfit.app?subject=Devenir%20testeur%20Jos'FIT"
        data-testid="gate-contact"
        className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-emerald-400 hover:text-emerald-300 transition-colors"
      >
        <Mail className="h-4 w-4" /> {t.launch.contact}
      </a>
    </div>
  );
};

export const AppLaunchProvider = ({ children }) => {
  const { t } = useLang();
  const [limitOpen, setLimitOpen] = useState(false);
  const [expiredOpen, setExpiredOpen] = useState(false);
  const [remainingLeft, setRemainingLeft] = useState(0);
  const [activeUntil, setActiveUntil] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const winRef = useRef(null);
  const timerRef = useRef(null);

  const getCount = () => Number(localStorage.getItem(STORAGE_KEY) || 0);

  const endSession = useCallback(() => {
    try {
      if (winRef.current && !winRef.current.closed) winRef.current.close();
    } catch (e) {}
    winRef.current = null;
    setActiveUntil(null);
    setRemainingLeft(Math.max(0, MAX_LAUNCHES - getCount()));
    setExpiredOpen(true);
  }, []);

  const launchApp = useCallback(() => {
    const count = getCount();
    if (count >= MAX_LAUNCHES) {
      setLimitOpen(true);
      return;
    }
    const win = window.open(APP_URL, "_blank");
    if (!win) {
      toast.error(t.launch.popup);
      return;
    }
    const newCount = count + 1;
    localStorage.setItem(STORAGE_KEY, String(newCount));
    winRef.current = win;
    setActiveUntil(Date.now() + DURATION_MS);
    toast.success(t.launch.launched.replace("{n}", MAX_LAUNCHES - newCount));
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(endSession, DURATION_MS);
  }, [endSession, t]);

  useEffect(() => {
    if (!activeUntil) {
      setCountdown(0);
      return;
    }
    const id = setInterval(() => {
      const left = Math.max(0, Math.round((activeUntil - Date.now()) / 1000));
      setCountdown(left);
    }, 500);
    return () => clearInterval(id);
  }, [activeUntil]);

  useEffect(() => {
    if (!activeUntil) return;
    const id = setInterval(() => {
      if (winRef.current && winRef.current.closed) {
        if (timerRef.current) clearTimeout(timerRef.current);
        winRef.current = null;
        setActiveUntil(null);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [activeUntil]);

  const mm = String(Math.floor(countdown / 60));
  const ss = String(countdown % 60).padStart(2, "0");

  return (
    <AppLaunchContext.Provider value={{ launchApp }}>
      {children}

      <AnimatePresence>
        {activeUntil && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            data-testid="trial-countdown"
            className="fixed bottom-6 right-6 z-[60] flex items-center gap-3 rounded-full border border-emerald-400/30 bg-[#0d1210]/90 backdrop-blur-xl px-5 py-3 shadow-2xl"
          >
            <Timer className="h-5 w-5 text-emerald-400" />
            <div className="leading-tight">
              <p className="text-[10px] uppercase tracking-wider text-slate-400">
                {t.launch.inProgress}
              </p>
              <p className="font-outfit text-lg font-bold text-white tabular-nums">
                {mm}:{ss}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Dialog open={limitOpen} onOpenChange={setLimitOpen}>
        <DialogContent
          data-testid="launch-limit-modal"
          className="border-white/10 bg-[#0d1210] text-white sm:max-w-md"
        >
          <DialogHeader>
            <span className="grid place-items-center h-12 w-12 rounded-2xl bg-emerald-500/10 text-emerald-400 ring-1 ring-emerald-400/20">
              <Lock className="h-6 w-6" />
            </span>
            <DialogTitle className="mt-4 font-outfit text-2xl font-extrabold">
              {t.launch.limitTitle}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {t.launch.limitDesc}
            </DialogDescription>
          </DialogHeader>
          <TesterButtons />
        </DialogContent>
      </Dialog>

      <Dialog open={expiredOpen} onOpenChange={setExpiredOpen}>
        <DialogContent
          data-testid="launch-expired-modal"
          className="border-white/10 bg-[#0d1210] text-white sm:max-w-md"
        >
          <DialogHeader>
            <span className="grid place-items-center h-12 w-12 rounded-2xl bg-emerald-500/10 text-emerald-400 ring-1 ring-emerald-400/20">
              <Timer className="h-6 w-6" />
            </span>
            <DialogTitle className="mt-4 font-outfit text-2xl font-extrabold">
              {t.launch.expiredTitle}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {remainingLeft > 0
                ? t.launch.expiredRemaining.replace("{n}", remainingLeft)
                : t.launch.expiredNone}
            </DialogDescription>
          </DialogHeader>
          <TesterButtons />
        </DialogContent>
      </Dialog>
    </AppLaunchContext.Provider>
  );
};
